import { useState, useEffect, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import { getClientesActivos, crearClienteMinimal } from "../../utils/clientes";
import { getFacturasEmitidas, getFacturasRecibidas } from "../../utils/conciliacionSII";
import { getEmpleados } from "../../utils/empleados";
import { crearMovimiento } from "../../utils/flujoCaja";
import { actualizarMatch, confirmarMovimiento, getFacturasYaVinculadas } from "../../utils/movimientosBancarios";
import { crearPagosEmpleado, TIPOS_PAGO_EMPLEADO, TIPOS_REQUIEREN_PERIODO } from "../../utils/pagosEmpleados";
import { getPlanCobros, getPlanPagos, actualizarCuotaCobro, actualizarCuotaPago } from "../../utils/planCobros";
import { getProveedores, crearProveedor } from "../../utils/proveedores";
import { client } from "../../utils/supabase";
import { useCurrency } from "../../utils/useCurrency";
import ProveedorSelector from "./ProveedorSelector";

const ROLES_PAGO_EMPLEADO = [ROLES.GERENCIA, ROLES.ADMINISTRATIVO, ROLES.SUPERADMIN];

// Path "Pago institucional": pagos a entidades sin DTE (PREVIRED, SII, banco, etc.).
// cuenta_er solo se setea para 'banco' — los pagos previsionales y tributarios son
// pasivos, no gastos operativos del Estado de Resultados.
const INSTITUCIONES = [
  {
    value: "previred",
    label: "PREVIRED",
    categoria_pago: "cotizaciones_previsionales",
    cuenta_er: null,
    requierePeriodo: true,
  },
  {
    value: "sii_declaracion",
    label: "SII — Declaración mensual",
    categoria_pago: "pago_impuesto",
    cuenta_er: null,
    requierePeriodo: true,
  },
  { value: "tesoreria", label: "Tesorería", categoria_pago: "pago_impuesto", cuenta_er: null, requierePeriodo: false },
  {
    value: "banco",
    label: "Banco — comisión/mantención",
    categoria_pago: "pago_banco",
    cuenta_er: "gastos_bancarios",
    requierePeriodo: false,
  },
  { value: "otro", label: "Otro", categoria_pago: "otro", cuenta_er: null, requierePeriodo: false },
];

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const parseFechaLocal = (str) => {
  if (!str) return new Date();
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const nuevaLineaComponente = (movimiento, tipoDefault = "sueldo") => {
  const fecha = parseFechaLocal(movimiento?.fecha);
  return {
    tipo: tipoDefault,
    periodo_año: fecha.getFullYear(),
    periodo_mes: fecha.getMonth() + 1,
    monto: "",
    notas: "",
  };
};

const VincularMovimientoModal = ({ show, onClose, movimiento, onSuccess }) => {
  const { pais, rol, id: usuarioId } = useAuthContext();
  const enteFiscal = pais === "Chile" ? "SII" : "SUNAT";
  const { formatAmount } = useCurrency();
  const puedePagarEmpleado = ROLES_PAGO_EMPLEADO.includes(rol);

  const [tipoVinculacion, setTipoVinculacion] = useState(movimiento?.tipo === "egreso" ? "pago" : "cobro");
  const [facturas, setFacturas] = useState([]);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  // Selección múltiple. Cuando length===1, contiene la misma que facturaSeleccionada
  // (single mode preservado). Cuando length>=2, activa modo múltiple (sin cuotas, mismo
  // proveedor/cliente, suma exacta). Persistencia: tabla movimiento_bancario_facturas.
  const [facturasSeleccionadas, setFacturasSeleccionadas] = useState([]);
  const [cuotas, setCuotas] = useState([]);
  const [cuotaSeleccionada, setCuotaSeleccionada] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(false);

  const [empleados, setEmpleados] = useState([]);
  const [empleadoId, setEmpleadoId] = useState(null);
  const [empleadoBusqueda, setEmpleadoBusqueda] = useState("");
  const [componentes, setComponentes] = useState([]);

  // Modo de compra en "Pago a proveedor": "sii" (factura SII) o "externa" (doc manual extranjero)
  const [modoCompra, setModoCompra] = useState("sii");
  const [proveedores, setProveedores] = useState([]);
  const [proveedorId, setProveedorId] = useState(null);
  const [modoNuevoProv, setModoNuevoProv] = useState(false);
  const [nuevoProvNombre, setNuevoProvNombre] = useState("");
  const [nuevoProvRut, setNuevoProvRut] = useState("");
  const [extFecha, setExtFecha] = useState("");
  const [extNumeroDoc, setExtNumeroDoc] = useState("");
  const [extConcepto, setExtConcepto] = useState("");
  const [extCuentaER, setExtCuentaER] = useState("");
  const [extArchivo, setExtArchivo] = useState(null);

  // Selector inline de entidad cuando la factura SII no tiene cliente_id (cobro) o proveedor_id (pago).
  // Sin esta entidad no se puede crear el asiento en movimientos_caja (Opción A).
  const [entidadFallbackOptions, setEntidadFallbackOptions] = useState([]);
  const [entidadFallbackId, setEntidadFallbackId] = useState(null);
  const [entidadFallbackBusqueda, setEntidadFallbackBusqueda] = useState("");

  // Modo "Sin factura": gastos operativos legítimos sin DTE (café con cliente, taxi, propinas).
  const [sfConcepto, setSfConcepto] = useState("");
  const [sfCuentaER, setSfCuentaER] = useState("");
  const [sfComercio, setSfComercio] = useState("");
  const [sfProveedorId, setSfProveedorId] = useState(null);
  const [sfProveedorNombre, setSfProveedorNombre] = useState("");
  const [acProveedorId, setAcProveedorId] = useState(null);
  const [acProveedorNombre, setAcProveedorNombre] = useState("");
  const [acNota, setAcNota] = useState("");

  // Crear proveedor desde el selector inline del path Pago SII cuando el RUT no existe en la tabla
  // proveedores (mismo patrón que "+ Nuevo proveedor" del path Factura externa).
  const [creandoProvSII, setCreandoProvSII] = useState(false);
  const [provSIINombre, setProvSIINombre] = useState("");
  const [provSIIRut, setProvSIIRut] = useState("");

  // Crear cliente desde el selector inline del path Cobro SII (simétrico al "+ Nuevo proveedor").
  const [creandoClienteSII, setCreandoClienteSII] = useState(false);
  const [clienteSIIEmpresa, setClienteSIIEmpresa] = useState("");
  const [clienteSIIRut, setClienteSIIRut] = useState("");

  // Modo "Pago institucional": PREVIRED, SII, Tesorería, banco, otros.
  const añoActualPI = new Date().getFullYear();
  const añosPI = [añoActualPI, añoActualPI + 1];
  const [piInstitucion, setPiInstitucion] = useState("");
  const [piPeriodoMes, setPiPeriodoMes] = useState(null);
  const [piPeriodoAño, setPiPeriodoAño] = useState(añoActualPI);
  const [piNotas, setPiNotas] = useState("");

  const monto = Number(movimiento?.monto || 0);
  const moneda = movimiento?.moneda || (pais === "Perú" ? "PEN" : "CLP");
  const esCobro = tipoVinculacion === "cobro";
  const esEmpleado = tipoVinculacion === "empleado";
  const esEgreso = movimiento?.tipo === "egreso";
  const esPagoExterna = tipoVinculacion === "pago" && modoCompra === "externa";
  const esPagoSinFactura = tipoVinculacion === "pago" && modoCompra === "sin_factura";
  const esPagoACuenta = tipoVinculacion === "pago" && modoCompra === "a_cuenta";
  const esPagoInstitucional = tipoVinculacion === "institucional";

  useEffect(() => {
    if (!show || !pais) return;
    setFacturaSeleccionada(null);
    setFacturasSeleccionadas([]);
    setCuotas([]);
    setCuotaSeleccionada(null);
    setBusqueda("");
    setEmpleadoId(null);
    setEmpleadoBusqueda("");
    setComponentes([{ ...nuevaLineaComponente(movimiento), monto }]);
    setModoCompra("sii");
    setProveedorId(null);
    setModoNuevoProv(false);
    setNuevoProvNombre("");
    setNuevoProvRut("");
    setExtFecha(movimiento?.fecha || "");
    setExtNumeroDoc("");
    setExtConcepto("");
    setExtCuentaER("");
    setExtArchivo(null);
    setEntidadFallbackId(null);
    setEntidadFallbackBusqueda("");
    setEntidadFallbackOptions([]);
    setSfConcepto("");
    setSfCuentaER("");
    setSfComercio("");
    setSfProveedorId(null);
    setSfProveedorNombre("");
    setAcProveedorId(null);
    setAcProveedorNombre("");
    setAcNota("");
    setCreandoProvSII(false);
    setProvSIINombre("");
    setProvSIIRut("");
    setCreandoClienteSII(false);
    setClienteSIIEmpresa("");
    setClienteSIIRut("");
    setPiInstitucion("");
    setPiPeriodoMes(null);
    setPiPeriodoAño(añoActualPI);
    setPiNotas("");
    if (tipoVinculacion === "empleado") cargarEmpleados();
    else cargarFacturas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, tipoVinculacion, pais, movimiento?.id]);

  // Cargar proveedores al entrar por primera vez en cualquier path "Pago a proveedor"
  // que use selector (externa, sin_factura, a_cuenta).
  useEffect(() => {
    if (!show || !pais) return;
    if (tipoVinculacion !== "pago") return;
    if (modoCompra !== "externa" && modoCompra !== "sin_factura" && modoCompra !== "a_cuenta") return;
    if (proveedores.length > 0) return;
    (async () => {
      try {
        const data = await getProveedores(pais);
        setProveedores(data);
      } catch {
        toast.error("Error cargando proveedores");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, pais, tipoVinculacion, modoCompra]);

  // Auto-pre-seleccionar proveedor extranjero por descripción del movimiento (best effort)
  useEffect(() => {
    if (!esPagoExterna || proveedorId || proveedores.length === 0) return;
    const desc = (movimiento?.descripcion_raw || "").toLowerCase();
    if (!desc) return;
    const match = proveedores
      .filter((p) => p.es_extranjero)
      .find((p) => {
        const nombre = (p.nombre || "").toLowerCase();
        const alias = (p.alias || "").toLowerCase();
        return (nombre && desc.includes(nombre)) || (alias && desc.includes(alias));
      });
    if (match) {
      setProveedorId(match.id);
      if (match.cuenta_er_default && !extCuentaER) setExtCuentaER(match.cuenta_er_default);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esPagoExterna, proveedores, movimiento?.descripcion_raw]);

  // Path SII: ¿la factura seleccionada ya tiene la entidad cargada por el auto-match de sii-sync?
  // (cliente_id si esCobro, proveedor_id si pago SII). Si no, hace falta el selector inline.
  // Solo aplica en modo single (1 factura) — en múltiple las validaciones del toggle ya
  // exigen entidad cargada en todas.
  const esModoMultiple = facturasSeleccionadas.length >= 2;
  const facturaTieneEntidad =
    facturaSeleccionada && (esCobro ? facturaSeleccionada.cliente_id : facturaSeleccionada.proveedor_id);
  const necesitaSelectorEntidad =
    !esEmpleado && !esPagoExterna && !esModoMultiple && facturaSeleccionada && !facturaTieneEntidad;
  const entidadResueltaId =
    (esCobro ? facturaSeleccionada?.cliente_id : facturaSeleccionada?.proveedor_id) || entidadFallbackId;

  // Modo múltiple: suma de los montos seleccionados y diferencia contra el monto del banco.
  const sumaMontosSeleccionados = facturasSeleccionadas.reduce((s, f) => s + Number(f.monto_total || 0), 0);
  const diferenciaMontos = monto - sumaMontosSeleccionados;
  const sumaCuadraExacto = Math.abs(diferenciaMontos) < 0.5;

  // Cargar opciones de entidad fallback (clientes para cobro, proveedores para pago SII)
  useEffect(() => {
    if (!necesitaSelectorEntidad) return;
    if (entidadFallbackOptions.length > 0) return;
    (async () => {
      try {
        const data = esCobro ? await getClientesActivos(pais) : await getProveedores(pais);
        setEntidadFallbackOptions(data);
      } catch {
        toast.error("Error cargando opciones de entidad");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [necesitaSelectorEntidad, esCobro, pais]);

  const cargarFacturas = async () => {
    setLoading(true);
    try {
      const [data, vinculadas] = await Promise.all([
        esCobro ? getFacturasEmitidas(pais) : getFacturasRecibidas(pais),
        getFacturasYaVinculadas(pais),
      ]);
      const yaVinculadas = esCobro ? vinculadas.emitidas : vinculadas.recibidas;
      setFacturas(data.filter((f) => !yaVinculadas.has(f.id)));
    } catch {
      toast.error("Error cargando facturas");
    } finally {
      setLoading(false);
    }
  };

  const cargarEmpleados = async () => {
    setLoading(true);
    try {
      const data = await getEmpleados(pais);
      const fechaMov = parseFechaLocal(movimiento?.fecha);
      const activos = data.filter((e) => !e.fin_contrato || parseFechaLocal(e.fin_contrato) >= fechaMov);
      setEmpleados(activos);
      const desc = (movimiento?.descripcion_raw || "").toLowerCase();
      const match = activos.find((e) => {
        const nombre = (e.nombre || "").toLowerCase();
        const apellido = (e.apellido || "").toLowerCase();
        return nombre && apellido && desc.includes(nombre) && desc.includes(apellido);
      });
      if (match) setEmpleadoId(match.id);
    } catch {
      toast.error("Error cargando empleados");
    } finally {
      setLoading(false);
    }
  };

  // Toggle de selección. Click en factura ya seleccionada la quita; click en otra la agrega.
  // Modo múltiple (length>=2): valida que todas sean del mismo proveedor/cliente y tengan
  // entidad cargada (no NULL). En modo single se preserva el flujo actual con cuotas.
  const handleSeleccionarFactura = async (f) => {
    const yaEsta = facturasSeleccionadas.some((x) => x.id === f.id);

    // QUITAR
    if (yaEsta) {
      const restantes = facturasSeleccionadas.filter((x) => x.id !== f.id);
      setFacturasSeleccionadas(restantes);
      setCuotaSeleccionada(null);
      if (restantes.length === 0) {
        setFacturaSeleccionada(null);
        setCuotas([]);
      } else if (restantes.length === 1) {
        // Volvemos a single con la que quedó: cargar cuotas si tiene
        const unica = restantes[0];
        setFacturaSeleccionada(unica);
        try {
          const plan = esCobro ? await getPlanCobros(unica.id) : await getPlanPagos(unica.id);
          setCuotas(plan.filter((c) => c.estado === "pendiente"));
        } catch {
          setCuotas([]);
        }
      } else {
        setFacturaSeleccionada(restantes.at(-1));
        setCuotas([]);
      }
      return;
    }

    // AGREGAR — validaciones para modo múltiple
    if (facturasSeleccionadas.length > 0) {
      const entidadCampo = esCobro ? "cliente_id" : "proveedor_id";
      const entidadEsperada = facturasSeleccionadas[0][entidadCampo];
      if (!f[entidadCampo]) {
        return toast.error(
          `La factura no tiene ${esCobro ? "cliente" : "proveedor"} asociado. En modo múltiple, vinculá esta factura primero en single para asignarle entidad.`
        );
      }
      if (entidadEsperada && f[entidadCampo] !== entidadEsperada) {
        return toast.error(`Todas las facturas deben ser del mismo ${esCobro ? "cliente" : "proveedor"}.`);
      }
    }

    const nueva = [...facturasSeleccionadas, f];
    setFacturasSeleccionadas(nueva);
    setFacturaSeleccionada(f);
    setCuotaSeleccionada(null);
    setEntidadFallbackId(null);
    setEntidadFallbackBusqueda("");
    setCreandoProvSII(false);
    setProvSIINombre("");
    setProvSIIRut("");
    setCreandoClienteSII(false);
    setClienteSIIEmpresa("");
    setClienteSIIRut("");

    if (nueva.length === 1) {
      try {
        const plan = esCobro ? await getPlanCobros(f.id) : await getPlanPagos(f.id);
        setCuotas(plan.filter((c) => c.estado === "pendiente"));
      } catch {
        setCuotas([]);
      }
    } else {
      // Modo múltiple: sin cuotas
      setCuotas([]);
    }
  };

  // Crea un proveedor desde el selector inline del path Pago SII y lo deja seleccionado.
  // El nombre y el RUT se pre-cargan desde la factura SII para minimizar fricción.
  const handleCrearProveedorDesdeSII = async () => {
    if (!provSIINombre.trim()) return toast.error("Ingresá el nombre del proveedor");
    try {
      const nuevo = await crearProveedor({
        pais,
        nombre: provSIINombre.trim(),
        rut_ruc: provSIIRut.trim() || null,
        habilitado: true,
      });
      if (!nuevo?.id) throw new Error("No se pudo crear el proveedor");
      setEntidadFallbackOptions((prev) =>
        [...prev, nuevo].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
      );
      setEntidadFallbackId(nuevo.id);
      setCreandoProvSII(false);
      setProvSIINombre("");
      setProvSIIRut("");
      toast.success("Proveedor creado y seleccionado");
    } catch (err) {
      toast.error("Error creando proveedor: " + (err.message || ""));
    }
  };

  // Simétrico al anterior pero para clientes (path Cobro SII).
  const handleCrearClienteDesdeSII = async () => {
    if (!clienteSIIEmpresa.trim()) return toast.error("Ingresá la razón social del cliente");
    try {
      const nuevo = await crearClienteMinimal({
        pais,
        empresa: clienteSIIEmpresa.trim(),
        nombre: clienteSIIEmpresa.trim(),
        rut: clienteSIIRut.trim() || null,
      });
      if (!nuevo?.id) throw new Error("No se pudo crear el cliente");
      setEntidadFallbackOptions((prev) =>
        [...prev, nuevo].sort((a, b) => (a.empresa || a.nombre || "").localeCompare(b.empresa || b.nombre || ""))
      );
      setEntidadFallbackId(nuevo.id);
      setCreandoClienteSII(false);
      setClienteSIIEmpresa("");
      setClienteSIIRut("");
      toast.success("Cliente creado y seleccionado");
    } catch (err) {
      toast.error("Error creando cliente: " + (err.message || ""));
    }
  };

  const actualizarComponente = (idx, campo, valor) => {
    setComponentes((prev) => prev.map((c, i) => (i === idx ? { ...c, [campo]: valor } : c)));
  };
  const agregarComponente = () => {
    const restante = monto - totalComponentes;
    setComponentes((prev) => [
      ...prev,
      { ...nuevaLineaComponente(movimiento, "bono"), monto: restante > 0 ? restante : "" },
    ]);
  };
  const eliminarComponente = (idx) => {
    setComponentes((prev) => prev.filter((_, i) => i !== idx));
  };

  const totalComponentes = useMemo(() => componentes.reduce((s, c) => s + Number(c.monto || 0), 0), [componentes]);
  const diferencia = monto - totalComponentes;
  const cuadra = Math.abs(diferencia) < 0.5;

  const handleConfirmarEmpleado = async () => {
    if (!empleadoId) return toast.error("Seleccioná un empleado");
    if (componentes.length === 0) return toast.error("Agregá al menos un componente");
    if (!cuadra) {
      return toast.error(
        `La suma (${formatAmount(totalComponentes, moneda)}) debe igualar el monto del movimiento (${formatAmount(
          monto,
          moneda
        )})`
      );
    }
    for (const c of componentes) {
      if (!c.tipo) return toast.error("Faltó tipo en un componente");
      if (TIPOS_REQUIEREN_PERIODO.includes(c.tipo) && (!c.periodo_año || !c.periodo_mes)) {
        return toast.error(`Tipo "${c.tipo}" requiere período (año/mes)`);
      }
      if (!c.monto || Number(c.monto) <= 0) return toast.error("Monto debe ser > 0 en todos los componentes");
    }
    setLoading(true);
    try {
      const rows = componentes.map((c) => ({
        empleado_id: empleadoId,
        tipo: c.tipo,
        periodo_año: c.periodo_año ? Number(c.periodo_año) : null,
        periodo_mes: c.periodo_mes ? Number(c.periodo_mes) : null,
        monto: Number(c.monto),
        moneda,
        fecha_pago: movimiento.fecha,
        movimiento_bancario_id: movimiento.id,
        notas: c.notas?.trim() ? c.notas.trim() : null,
        created_by: usuarioId || null,
      }));
      await crearPagosEmpleado(rows);
      toast.success("Pago a empleado registrado");
      // Empleado: el badge E depende de pagosEmpleadosRegistrados (state separado en
      // el padre). Flag _empleado=true le indica que haga cargarDatos en lugar de
      // actualización optimista.
      onSuccess?.({ id: movimiento.id, _empleado: true });
      onClose();
    } catch (err) {
      toast.error("Error al registrar pago: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const subirArchivo = async (file) => {
    const ext = file.name.split(".").pop();
    const path = `externas/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await client.storage.from("facturas").upload(path, file);
    if (error) throw error;
    const { data } = client.storage.from("facturas").getPublicUrl(path);
    return data.publicUrl;
  };

  const handleConfirmarFacturaExterna = async () => {
    if (!proveedorId && !modoNuevoProv) return toast.error("Seleccioná un proveedor o creá uno nuevo");
    if (modoNuevoProv && !nuevoProvNombre.trim()) return toast.error("Ingresá el nombre del nuevo proveedor");
    if (!extFecha) return toast.error("Ingresá la fecha del documento");
    if (!extConcepto.trim()) return toast.error("Ingresá el concepto");
    if (!extCuentaER) return toast.error("Seleccioná la cuenta del Estado de Resultados");
    if (!extArchivo) return toast.error("Subí el documento (PDF/imagen)");
    setLoading(true);
    try {
      let provId = proveedorId;
      let provNombre = proveedores.find((p) => p.id === proveedorId)?.nombre || "";
      if (modoNuevoProv) {
        const nuevo = await crearProveedor({
          pais,
          nombre: nuevoProvNombre.trim(),
          rut_ruc: nuevoProvRut.trim() || null,
          es_extranjero: true,
          habilitado: true,
          cuenta_er_default: extCuentaER,
          moneda_recurrente: moneda,
        });
        if (!nuevo?.id) throw new Error("No se pudo crear el proveedor");
        provId = nuevo.id;
        provNombre = nuevo.nombre;
        setProveedores((prev) => [...prev, nuevo].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      }

      const archivoUrl = await subirArchivo(extArchivo);

      const { data: fe, error: feErr } = await client
        .from("facturas_externas")
        .insert({
          pais,
          proveedor_id: Number(provId),
          proveedor_nombre: provNombre,
          concepto: extConcepto.trim(),
          numero_documento: extNumeroDoc.trim() || null,
          fecha_emision: extFecha,
          monto_neto: monto,
          monto_impuesto: 0,
          monto_total: monto,
          moneda,
          cuenta_er: extCuentaER,
          archivo_url: archivoUrl,
          estado: "pagada",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        })
        .select()
        .single();
      if (feErr) throw feErr;

      await actualizarMatch(movimiento.id, {
        match_entidad_tipo: "proveedor",
        match_entidad_id: Number(provId),
        match_entidad_nombre: provNombre,
        match_factura_tipo: "factura_externa",
        match_factura_id: fe.id,
        match_confianza: 1,
        match_razon: "factura_externa_manual",
      });

      const nuevoCaja = await crearMovimiento({
        pais,
        tipo: "pago",
        fecha: movimiento.fecha,
        monto,
        monto_neto: monto,
        monto_impuesto: 0,
        moneda,
        estado: "confirmado",
        descripcion: `${provNombre} — ${extConcepto.trim()}`,
        entidad_tipo: "proveedor",
        entidad_id: Number(provId),
        entidad_nombre: provNombre,
        categoria_pago: "pago_proveedor",
        origen: "clay",
        movimiento_bancario_id: movimiento.id,
        registrado_por: usuarioId || null,
      });
      if (!nuevoCaja?.id) throw new Error("No se pudo crear el movimiento en caja");

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: nuevoCaja.id,
        procesado_por: usuarioId || null,
      });

      toast.success("Factura externa registrada y movimiento confirmado");
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: nuevoCaja.id,
        movimiento_caja: { categoria_pago: "pago_proveedor" },
        match_factura_tipo: "factura_externa",
        match_entidad_nombre: provNombre,
        factura_externa: [{ numero_documento: extNumeroDoc.trim() || null }],
      });
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarSinFactura = async () => {
    if (!sfProveedorId) return toast.error("Seleccioná un proveedor");
    if (!sfConcepto.trim()) return toast.error("Ingresá el concepto del gasto");
    if (!sfCuentaER) return toast.error("Seleccioná la cuenta del Estado de Resultados");
    setLoading(true);
    try {
      // Idempotencia por movimiento_bancario_id
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaId = existente?.id || null;
      if (!existente) {
        const movCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto,
          monto_neto: monto,
          monto_impuesto: 0,
          moneda,
          estado: "confirmado",
          descripcion: sfConcepto.trim(),
          entidad_tipo: "proveedor",
          entidad_id: sfProveedorId,
          entidad_nombre: sfProveedorNombre,
          categoria_pago: "gasto_sin_factura",
          cuenta_er: sfCuentaER,
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
        movCajaId = movCaja.id;
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: movCajaId,
        procesado_por: usuarioId || null,
      });

      toast.success("Gasto sin factura registrado");
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: "gasto_sin_factura" },
      });
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarACuenta = async () => {
    if (!acProveedorId) return toast.error("Seleccioná un proveedor");
    setLoading(true);
    try {
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaId = existente?.id || null;
      if (!existente) {
        const movCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto,
          monto_neto: monto,
          monto_impuesto: 0,
          moneda,
          estado: "confirmado",
          descripcion: acNota.trim() || `Pago a cuenta — ${acProveedorNombre}`,
          entidad_tipo: "proveedor",
          entidad_id: acProveedorId,
          entidad_nombre: acProveedorNombre,
          categoria_pago: "pago_a_cuenta",
          cuenta_er: null,
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
        movCajaId = movCaja.id;
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: movCajaId,
        procesado_por: usuarioId || null,
      });

      toast.success("Pago a cuenta registrado");
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: "pago_a_cuenta" },
      });
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Modo múltiple: 1 mov bancario vincula a N facturas SII (mismo proveedor/cliente).
  // Las FKs directas en movimientos_bancarios.factura_sii_*_id quedan en NULL; las N
  // vinculaciones se registran en la tabla movimiento_bancario_facturas. Se crea 1 sola
  // fila en movimientos_caja con el monto del banco y monto_neto/monto_impuesto sumados.
  const handleConfirmarMultiple = async () => {
    if (!sumaCuadraExacto) {
      return toast.error("La suma de las facturas debe coincidir exacto con el monto del movimiento.");
    }
    const entidadCampo = esCobro ? "cliente_id" : "proveedor_id";
    const entidadId = facturasSeleccionadas[0][entidadCampo];
    const entidadNombre = esCobro
      ? facturasSeleccionadas[0].razon_social_receptor
      : facturasSeleccionadas[0].razon_social_emisor;
    setLoading(true);
    try {
      // 1. UPDATE movimientos_bancarios (FKs directas en NULL — son múltiples)
      const updateMov = {
        factura_sii_emitida_id: null,
        factura_sii_recibida_id: null,
        estado: "procesado",
        procesado_por: usuarioId || null,
        procesado_at: new Date().toISOString(),
        match_entidad_tipo: null,
        match_entidad_id: null,
        match_entidad_nombre: null,
        match_factura_tipo: null,
        match_factura_id: null,
        match_confianza: null,
        match_razon: null,
      };
      await client.from("movimientos_bancarios").update(updateMov).eq("id", movimiento.id);

      // 2. INSERT N filas en movimiento_bancario_facturas
      const filasMBF = facturasSeleccionadas.map((f) => ({
        movimiento_bancario_id: movimiento.id,
        factura_tipo: esCobro ? "sii_emitida" : "sii_recibida",
        factura_id: f.id,
        monto: f.monto_total,
      }));
      const { error: errMBF } = await client.from("movimiento_bancario_facturas").insert(filasMBF);
      if (errMBF) throw errMBF;

      // 3. UPDATE cada factura SII: vinculada + plazopago/facturafechacobro
      for (const f of facturasSeleccionadas) {
        const tabla = esCobro ? "facturas_sii_emitidas" : "facturas_sii_recibidas";
        const update = {
          estado_vinculacion: "vinculada",
          vinculado_por: usuarioId || null,
          vinculado_at: new Date().toISOString(),
        };
        if (esCobro) update.facturafechacobro = movimiento.fecha;
        else update.plazopago = movimiento.fecha;
        const { error: errF } = await client.from(tabla).update(update).eq("id", f.id);
        if (errF) throw errF;
      }

      // 4. INSERT movimientos_caja (idempotente por movimiento_bancario_id)
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaIdMultiple = existente?.id || null;
      if (!existente) {
        const totalNeto = facturasSeleccionadas.reduce((s, f) => s + Number(f.monto_neto || 0), 0);
        const totalIva = facturasSeleccionadas.reduce((s, f) => s + Number(f.monto_iva || 0), 0);
        const folios = facturasSeleccionadas.map((f) => f.folio).join(", ");
        const movCaja = await crearMovimiento({
          pais,
          tipo: esCobro ? "cobro" : "pago",
          fecha: movimiento.fecha,
          monto, // del movimiento bancario
          monto_neto: totalNeto,
          monto_impuesto: totalIva,
          moneda: movimiento.moneda || (pais === "Chile" ? "CLP" : "PEN"),
          estado: "confirmado",
          descripcion: `${entidadNombre} — Folios ${folios}`,
          entidad_tipo: esCobro ? "cliente" : "proveedor",
          entidad_id: entidadId,
          entidad_nombre: entidadNombre,
          categoria_pago: esCobro ? "cobro_factura" : "pago_proveedor",
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (movCaja?.id) {
          await client.from("movimientos_bancarios").update({ movimiento_caja_id: movCaja.id }).eq("id", movimiento.id);
          movCajaIdMultiple = movCaja.id;
        }
      }

      toast.success(`${facturasSeleccionadas.length} facturas vinculadas`);
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        factura_sii_emitida_id: null,
        factura_sii_recibida_id: null,
        movimiento_caja_id: movCajaIdMultiple,
        movimiento_caja: { categoria_pago: esCobro ? "cobro_factura" : "pago_proveedor" },
      });
      onClose();
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Modo "Pago institucional": INSERT en movimientos_caja sin factura asociada.
  // Sin INSERT en movimiento_bancario_facturas (no hay factura). cuenta_er solo para banco.
  const handleConfirmarInstitucional = async () => {
    const inst = INSTITUCIONES.find((i) => i.value === piInstitucion);
    if (!inst) return toast.error("Seleccioná una institución");
    if (inst.requierePeriodo && (!piPeriodoMes || !piPeriodoAño)) {
      return toast.error(`${inst.label} requiere período (mes y año)`);
    }
    setLoading(true);
    try {
      // Idempotencia
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaId = existente?.id || null;
      if (!existente) {
        const periodoLabel =
          inst.requierePeriodo && piPeriodoMes && piPeriodoAño ? ` — ${MESES[piPeriodoMes - 1]} ${piPeriodoAño}` : "";
        const descripcion = `${inst.label}${periodoLabel}${piNotas.trim() ? ` (${piNotas.trim()})` : ""}`;
        const movCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto,
          monto_neto: monto,
          monto_impuesto: 0,
          moneda,
          estado: "confirmado",
          descripcion,
          entidad_tipo: "otro",
          entidad_id: null,
          entidad_nombre: inst.label,
          categoria_pago: inst.categoria_pago,
          cuenta_er: inst.cuenta_er,
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
        movCajaId = movCaja.id;
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: movCajaId,
        procesado_por: usuarioId || null,
      });

      toast.success(`Pago a ${inst.label} registrado`);
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: inst.categoria_pago },
      });
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = async () => {
    if (esEmpleado) return handleConfirmarEmpleado();
    if (esPagoExterna) return handleConfirmarFacturaExterna();
    if (esPagoSinFactura) return handleConfirmarSinFactura();
    if (esPagoACuenta) return handleConfirmarACuenta();
    if (esPagoInstitucional) return handleConfirmarInstitucional();
    if (esModoMultiple) return handleConfirmarMultiple();
    if (!facturaSeleccionada) return toast.error("Seleccioná una factura");
    if (!entidadResueltaId) {
      return toast.error(
        esCobro
          ? "La factura no tiene cliente asociado. Seleccioná uno en el listado."
          : "La factura no tiene proveedor asociado. Seleccioná uno en el listado."
      );
    }
    setLoading(true);
    try {
      // 1. Si la entidad fue elegida manualmente (auto-match de sii-sync había fallado),
      //    se persiste en la factura SII para que próximas consultas la encuentren.
      if (entidadFallbackId) {
        const tabla = esCobro ? "facturas_sii_emitidas" : "facturas_sii_recibidas";
        const campo = esCobro ? "cliente_id" : "proveedor_id";
        const { error: errEntidad } = await client
          .from(tabla)
          .update({ [campo]: entidadFallbackId })
          .eq("id", facturaSeleccionada.id);
        if (errEntidad) throw errEntidad;
      }

      // 2. UPDATE movimientos_bancarios (vinculación + procesado)
      const updateMov = {
        ...(esCobro
          ? { factura_sii_emitida_id: facturaSeleccionada.id }
          : { factura_sii_recibida_id: facturaSeleccionada.id }),
        estado: "procesado",
        procesado_por: usuarioId || null,
        procesado_at: new Date().toISOString(),
        match_entidad_tipo: null,
        match_entidad_id: null,
        match_entidad_nombre: null,
        match_factura_tipo: null,
        match_factura_id: null,
        match_confianza: null,
        match_razon: null,
      };
      if (cuotaSeleccionada) {
        Object.assign(
          updateMov,
          esCobro ? { plan_cobro_cuota_id: cuotaSeleccionada.id } : { plan_pago_cuota_id: cuotaSeleccionada.id }
        );
      }
      await client.from("movimientos_bancarios").update(updateMov).eq("id", movimiento.id);

      // 3. Marcar factura SII (fecha cobro emitida / plazo pago recibida)
      if (esCobro) {
        await client
          .from("facturas_sii_emitidas")
          .update({ facturafechacobro: movimiento.fecha })
          .eq("id", facturaSeleccionada.id);
      }

      if (cuotaSeleccionada) {
        const fechaReal = movimiento.fecha;
        const cambios = esCobro
          ? { estado: "cobrado", fecha_cobro_real: fechaReal, movimiento_bancario_id: movimiento.id }
          : { estado: "pagado", fecha_pago_real: fechaReal, movimiento_bancario_id: movimiento.id };
        await (esCobro
          ? actualizarCuotaCobro(cuotaSeleccionada.id, cambios)
          : actualizarCuotaPago(cuotaSeleccionada.id, cambios));
      } else if (!esCobro) {
        await client
          .from("facturas_sii_recibidas")
          .update({ plazopago: movimiento.fecha })
          .eq("id", facturaSeleccionada.id);
      }

      // 4. Marcar la factura como vinculada (siempre, con o sin cuota). Otros módulos
      //    consultan estado_vinculacion='vinculada' para filtrar las ya procesadas.
      await client
        .from(esCobro ? "facturas_sii_emitidas" : "facturas_sii_recibidas")
        .update({
          estado_vinculacion: "vinculada",
          vinculado_por: usuarioId || null,
          vinculado_at: new Date().toISOString(),
        })
        .eq("id", facturaSeleccionada.id);

      // 5. Opción A: crear el asiento en movimientos_caja si no existe (idempotente por movimiento_bancario_id).
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaIdSingle = existente?.id || null;
      if (!existente) {
        const entidadNombre = esCobro
          ? facturaSeleccionada.razon_social_receptor
          : facturaSeleccionada.razon_social_emisor;
        const movCaja = await crearMovimiento({
          pais,
          tipo: esCobro ? "cobro" : "pago",
          fecha: movimiento.fecha,
          monto: facturaSeleccionada.monto_total,
          monto_neto: facturaSeleccionada.monto_neto,
          monto_impuesto: facturaSeleccionada.monto_iva,
          moneda: movimiento.moneda || (pais === "Chile" ? "CLP" : "PEN"),
          estado: "confirmado",
          descripcion: `${entidadNombre} — Folio ${facturaSeleccionada.folio}`,
          entidad_tipo: esCobro ? "cliente" : "proveedor",
          entidad_id: entidadResueltaId,
          entidad_nombre: entidadNombre,
          categoria_pago: esCobro ? "cobro_factura" : "pago_proveedor",
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (movCaja?.id) {
          await client.from("movimientos_bancarios").update({ movimiento_caja_id: movCaja.id }).eq("id", movimiento.id);
          movCajaIdSingle = movCaja.id;
        }
      }

      // 6. Registro adicional en movimiento_bancario_facturas (consistencia con modo
      //    múltiple). Idempotente: skip si ya existe vinculación para este par.
      const { data: mbfExistente } = await client
        .from("movimiento_bancario_facturas")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .eq("factura_tipo", esCobro ? "sii_emitida" : "sii_recibida")
        .eq("factura_id", facturaSeleccionada.id)
        .maybeSingle();
      if (!mbfExistente) {
        await client.from("movimiento_bancario_facturas").insert({
          movimiento_bancario_id: movimiento.id,
          factura_tipo: esCobro ? "sii_emitida" : "sii_recibida",
          factura_id: facturaSeleccionada.id,
          monto: facturaSeleccionada.monto_total,
        });
      }

      toast.success("Movimiento vinculado correctamente");
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaIdSingle,
        movimiento_caja: { categoria_pago: esCobro ? "cobro_factura" : "pago_proveedor" },
        ...(esCobro
          ? {
              factura_sii_emitida_id: facturaSeleccionada.id,
              factura_emitida: {
                folio: facturaSeleccionada.folio,
                razon_social_receptor: facturaSeleccionada.razon_social_receptor,
              },
            }
          : {
              factura_sii_recibida_id: facturaSeleccionada.id,
              factura_recibida: {
                folio: facturaSeleccionada.folio,
                razon_social_emisor: facturaSeleccionada.razon_social_emisor,
              },
            }),
      });
      onClose();
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const facturasFiltradas = facturas.filter((f) => {
    if (!busqueda) return true;
    const texto = busqueda.trim().toLowerCase();
    const nombre = esCobro
      ? (f.razon_social_receptor || "").toLowerCase()
      : (f.razon_social_emisor || "").toLowerCase();
    if (nombre.includes(texto)) return true;
    if (String(f.folio).includes(texto)) return true;
    // Monto: comparar dígitos del query (descartando . , espacios) contra el monto sin separadores
    const queryDigitos = texto.replace(/\D/g, "");
    if (queryDigitos && String(f.monto_total).includes(queryDigitos)) return true;
    return false;
  });

  const empleadosFiltrados = empleados.filter((e) => {
    if (!empleadoBusqueda) return true;
    const texto = empleadoBusqueda.toLowerCase();
    const full = `${e.nombre || ""} ${e.apellido || ""}`.toLowerCase();
    return full.includes(texto);
  });

  const tabs = [
    { key: "cobro", label: "Cobro de cliente" },
    { key: "pago", label: "Pago a proveedor" },
    ...(esEgreso && puedePagarEmpleado ? [{ key: "empleado", label: "Pago a empleado" }] : []),
    ...(esEgreso ? [{ key: "institucional", label: "Pago institucional" }] : []),
  ];

  const confirmarDisabled =
    loading ||
    (esEmpleado
      ? !empleadoId || !cuadra || componentes.length === 0
      : esPagoExterna
        ? (!proveedorId && !modoNuevoProv) ||
          (modoNuevoProv && !nuevoProvNombre.trim()) ||
          !extFecha ||
          !extConcepto.trim() ||
          !extCuentaER ||
          !extArchivo
        : esPagoSinFactura
          ? !sfProveedorId || !sfConcepto.trim() || !sfCuentaER
          : esPagoACuenta
            ? !acProveedorId
            : esPagoInstitucional
              ? !piInstitucion ||
                (!!INSTITUCIONES.find((i) => i.value === piInstitucion)?.requierePeriodo &&
                  (!piPeriodoMes || !piPeriodoAño))
              : esModoMultiple
                ? !sumaCuadraExacto
                : !facturaSeleccionada || !entidadResueltaId);

  if (!movimiento) return null;

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Vincular movimiento bancario</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          style={{
            background: "#f8f9fa",
            borderRadius: 6,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: "0.85rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontWeight: 600 }}>{movimiento.descripcion_raw}</div>
            <div style={{ color: "#888", fontSize: "0.78rem" }}>{movimiento.fecha}</div>
          </div>
          <div
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              color: movimiento.tipo === "ingreso" ? "#27ae60" : "#c0392b",
            }}
          >
            {formatAmount(monto, moneda)}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16, width: "fit-content" }}>
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTipoVinculacion(t.key)}
              style={{
                width: 140,
                padding: "6px 0",
                fontSize: "0.82rem",
                fontWeight: 600,
                border: "1px solid #dee2e6",
                borderRadius: 6,
                cursor: "pointer",
                background: tipoVinculacion === t.key ? "#2c3e50" : "transparent",
                color: tipoVinculacion === t.key ? "#fff" : "#666",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {!esEmpleado && tipoVinculacion === "pago" && (
          <div style={{ display: "flex", gap: 8, marginBottom: 16, width: "fit-content" }}>
            {[
              { key: "sii", label: `Factura ${enteFiscal}` },
              { key: "externa", label: "Factura externa" },
              { key: "sin_factura", label: "Sin factura" },
              { key: "a_cuenta", label: "A cuenta" },
            ].map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setModoCompra(m.key)}
                style={{
                  width: 140,
                  padding: "6px 0",
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  cursor: "pointer",
                  background: modoCompra === m.key ? "#2c3e50" : "transparent",
                  color: modoCompra === m.key ? "#fff" : "#666",
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}

        {!esEmpleado && !esPagoExterna && !esPagoSinFactura && !esPagoACuenta && !esPagoInstitucional && (
          <>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Buscar por nombre, folio o monto..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              style={{ marginBottom: 10 }}
            />

            <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
              {loading ? (
                <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Cargando...</div>
              ) : facturasFiltradas.length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin facturas</div>
              ) : (
                facturasFiltradas.map((f) => {
                  const nombre = esCobro ? f.razon_social_receptor : f.razon_social_emisor;
                  const seleccionada = facturasSeleccionadas.some((x) => x.id === f.id);
                  return (
                    <div
                      key={f.id}
                      onClick={() => handleSeleccionarFactura(f)}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontSize: "0.83rem",
                        borderBottom: "1px solid #f0f0f0",
                        background: seleccionada ? "#e8f4fd" : "transparent",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <span>{`${f.fecha_emision.substring(8, 10)}/${f.fecha_emision.substring(5, 7)}`}</span>
                        <span style={{ margin: "0 10px" }}>|</span>
                        <span>{nombre}</span>
                        <span style={{ marginLeft: 8 }}>Folio {f.folio}</span>
                      </div>
                      <div style={{ color: esCobro ? "#27ae60" : "#c0392b", fontWeight: 600 }}>
                        {formatAmount(f.monto_total, moneda)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {esModoMultiple && (
              <div
                style={{
                  marginTop: 14,
                  padding: 10,
                  borderRadius: 4,
                  background: sumaCuadraExacto ? "#e8f5e9" : "#fff3cd",
                  border: `1px solid ${sumaCuadraExacto ? "#a5d6a7" : "#f0d878"}`,
                }}
              >
                <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 8 }}>
                  Selección múltiple ({facturasSeleccionadas.length} facturas)
                </div>
                <div style={{ marginBottom: 8 }}>
                  {facturasSeleccionadas.map((f) => {
                    const nombre = esCobro ? f.razon_social_receptor : f.razon_social_emisor;
                    return (
                      <div
                        key={f.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "0.82rem",
                          padding: "3px 0",
                        }}
                      >
                        <span>
                          {`${f.fecha_emision.substring(8, 10)}/${f.fecha_emision.substring(5, 7)}`} | {nombre} Folio{" "}
                          {f.folio}
                        </span>
                        <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <span>{formatAmount(f.monto_total, moneda)}</span>
                          <button
                            type="button"
                            onClick={() => handleSeleccionarFactura(f)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#c0392b",
                              cursor: "pointer",
                              fontSize: "1rem",
                              padding: "0 4px",
                            }}
                            title="Quitar de la selección"
                          >
                            ×
                          </button>
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div
                  style={{
                    borderTop: "1px solid #ccc",
                    paddingTop: 6,
                    fontSize: "0.82rem",
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "2px 12px",
                  }}
                >
                  <span>Suma seleccionada</span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>
                    {formatAmount(sumaMontosSeleccionados, moneda)}
                  </span>
                  <span>Monto del movimiento</span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(monto, moneda)}</span>
                  <span style={{ fontWeight: 600 }}>Diferencia</span>
                  <span
                    style={{
                      textAlign: "right",
                      fontWeight: 700,
                      color: sumaCuadraExacto ? "#2e7d32" : "#c0392b",
                    }}
                  >
                    {sumaCuadraExacto ? "✓ exacta" : formatAmount(diferenciaMontos, moneda)}
                  </span>
                </div>
              </div>
            )}

            {necesitaSelectorEntidad && (
              <div
                style={{
                  marginTop: 14,
                  padding: 10,
                  borderRadius: 4,
                  background: "#fff3cd",
                  border: "1px solid #f0d878",
                }}
              >
                <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 6 }}>
                  La factura no tiene {esCobro ? "cliente" : "proveedor"} asociado — seleccioná uno:
                </div>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={`Buscar ${esCobro ? "cliente" : "proveedor"}...`}
                  value={entidadFallbackBusqueda}
                  onChange={(e) => setEntidadFallbackBusqueda(e.target.value)}
                  style={{ marginBottom: 6, fontSize: "0.82rem" }}
                />
                <div
                  style={{
                    maxHeight: 160,
                    overflowY: "auto",
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    background: "#fff",
                  }}
                >
                  {entidadFallbackOptions
                    .filter((opt) => {
                      if (!entidadFallbackBusqueda) return true;
                      const t = entidadFallbackBusqueda.toLowerCase();
                      const nombre = (esCobro ? opt.empresa || opt.nombre : opt.nombre) || "";
                      const rut = opt.rut || opt.rut_ruc || "";
                      return nombre.toLowerCase().includes(t) || rut.toLowerCase().includes(t);
                    })
                    .slice(0, 100)
                    .map((opt) => {
                      const sel = entidadFallbackId === opt.id;
                      const nombre = esCobro ? opt.empresa || opt.nombre : opt.nombre;
                      return (
                        <div
                          key={opt.id}
                          onClick={() => setEntidadFallbackId(opt.id)}
                          style={{
                            padding: "6px 12px",
                            cursor: "pointer",
                            fontSize: "0.82rem",
                            borderBottom: "1px solid #f0f0f0",
                            background: sel ? "#e8f4fd" : "transparent",
                            fontWeight: sel ? 600 : 400,
                          }}
                        >
                          {nombre}
                          {(opt.rut || opt.rut_ruc) && (
                            <span style={{ marginLeft: 8, fontSize: "0.72rem", color: "#888" }}>
                              {opt.rut || opt.rut_ruc}
                            </span>
                          )}
                        </div>
                      );
                    })}
                </div>

                {!esCobro &&
                  (!creandoProvSII ? (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        setProvSIINombre(facturaSeleccionada?.razon_social_emisor || "");
                        setProvSIIRut(facturaSeleccionada?.rut_emisor || "");
                        setCreandoProvSII(true);
                      }}
                      style={{ marginTop: 8, fontSize: "0.78rem" }}
                    >
                      + Nuevo proveedor
                    </Button>
                  ) : (
                    <div
                      style={{
                        marginTop: 8,
                        padding: 10,
                        border: "1px solid #dee2e6",
                        borderRadius: 4,
                        background: "#fafbfc",
                      }}
                    >
                      <div style={{ fontSize: "0.72rem", color: "#666", marginBottom: 6 }}>
                        Pre-cargado desde la factura {enteFiscal}. Verificá antes de crear.
                      </div>
                      <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Nombre / razón social"
                          value={provSIINombre}
                          onChange={(e) => setProvSIINombre(e.target.value)}
                          style={{ flex: 2, fontSize: "0.82rem" }}
                        />
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="RUT"
                          value={provSIIRut}
                          onChange={(e) => setProvSIIRut(e.target.value)}
                          style={{ flex: 1, fontSize: "0.82rem" }}
                        />
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={handleCrearProveedorDesdeSII}
                          disabled={!provSIINombre.trim()}
                          style={{ fontSize: "0.78rem" }}
                        >
                          Crear y seleccionar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            setCreandoProvSII(false);
                            setProvSIINombre("");
                            setProvSIIRut("");
                          }}
                          style={{ fontSize: "0.78rem" }}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ))}

                {esCobro &&
                  (!creandoClienteSII ? (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        setClienteSIIEmpresa(facturaSeleccionada?.razon_social_receptor || "");
                        setClienteSIIRut(facturaSeleccionada?.rut_receptor || "");
                        setCreandoClienteSII(true);
                      }}
                      style={{ marginTop: 8, fontSize: "0.78rem" }}
                    >
                      + Nuevo cliente
                    </Button>
                  ) : (
                    <div
                      style={{
                        marginTop: 8,
                        padding: 10,
                        border: "1px solid #dee2e6",
                        borderRadius: 4,
                        background: "#fafbfc",
                      }}
                    >
                      <div style={{ fontSize: "0.72rem", color: "#666", marginBottom: 6 }}>
                        Pre-cargado desde la factura {enteFiscal}. Verificá antes de crear.
                      </div>
                      <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Razón social"
                          value={clienteSIIEmpresa}
                          onChange={(e) => setClienteSIIEmpresa(e.target.value)}
                          style={{ flex: 2, fontSize: "0.82rem" }}
                        />
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="RUT"
                          value={clienteSIIRut}
                          onChange={(e) => setClienteSIIRut(e.target.value)}
                          style={{ flex: 1, fontSize: "0.82rem" }}
                        />
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={handleCrearClienteDesdeSII}
                          disabled={!clienteSIIEmpresa.trim()}
                          style={{ fontSize: "0.78rem" }}
                        >
                          Crear y seleccionar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            setCreandoClienteSII(false);
                            setClienteSIIEmpresa("");
                            setClienteSIIRut("");
                          }}
                          style={{ fontSize: "0.78rem" }}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {cuotas.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: "0.82rem", fontWeight: 600, marginBottom: 6 }}>
                  Plan de {esCobro ? "cobro" : "pago"} — seleccioná la cuota:
                </div>
                {cuotas.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => setCuotaSeleccionada(c)}
                    style={{
                      padding: "6px 12px",
                      cursor: "pointer",
                      fontSize: "0.82rem",
                      borderRadius: 4,
                      marginBottom: 4,
                      border: `1px solid ${cuotaSeleccionada?.id === c.id ? "#2c3e50" : "#dee2e6"}`,
                      background: cuotaSeleccionada?.id === c.id ? "#e8f4fd" : "#fff",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>
                      Cuota {c.numero_cuota} · vence {c.fecha_vencimiento}
                    </span>
                    <span style={{ fontWeight: 600 }}>{formatAmount(c.monto_cuota, moneda)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {esPagoExterna && (
          <>
            <ProveedorSelector
              value={proveedorId}
              onChange={(p) => {
                setProveedorId(p.id);
                if (p.cuenta_er_default && !extCuentaER) setExtCuentaER(p.cuenta_er_default);
              }}
              filter={(p) => p.es_extranjero}
              proveedores={proveedores}
              onCrearNuevo={() => setModoNuevoProv(true)}
              modoNuevo={modoNuevoProv}
              onModoNuevoChange={setModoNuevoProv}
              nuevoNombre={nuevoProvNombre}
              onNuevoNombreChange={setNuevoProvNombre}
              nuevoRut={nuevoProvRut}
              onNuevoRutChange={setNuevoProvRut}
              hintCreacion={`Se marca como extranjero (es_extranjero=true) y se registra con moneda ${moneda}.`}
            />

            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Fecha documento</div>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={extFecha}
                  onChange={(e) => setExtFecha(e.target.value)}
                  style={{ fontSize: "0.82rem" }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>N° documento (opcional)</div>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Ej: 3YK4QC1M-0005"
                  value={extNumeroDoc}
                  onChange={(e) => setExtNumeroDoc(e.target.value)}
                  style={{ fontSize: "0.82rem" }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Concepto</div>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Ej: Team plan - Standard Apr 18–May 18, 2026"
                value={extConcepto}
                onChange={(e) => setExtConcepto(e.target.value)}
                style={{ fontSize: "0.82rem" }}
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Cuenta Estado de Resultados</div>
              <select
                className="form-select form-select-sm"
                value={extCuentaER}
                onChange={(e) => setExtCuentaER(e.target.value)}
                style={{ fontSize: "0.82rem" }}
              >
                <option value="">Seleccionar cuenta…</option>
                {["Costos Fijos", "Costos Variables"].map((grupo) => (
                  <optgroup key={grupo} label={grupo}>
                    {CUENTAS_ER.filter((c) => c.grupo === grupo).map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Documento (PDF o imagen)</div>
              <input
                type="file"
                className="form-control form-control-sm"
                accept="application/pdf,image/*"
                onChange={(e) => setExtArchivo(e.target.files?.[0] || null)}
                style={{ fontSize: "0.82rem" }}
              />
              {extArchivo && (
                <div style={{ fontSize: "0.72rem", color: "#27ae60", marginTop: 4 }}>✓ {extArchivo.name}</div>
              )}
            </div>
          </>
        )}

        {esPagoSinFactura && (
          <>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#666",
                background: "#f8f9fa",
                borderRadius: 4,
                padding: "8px 10px",
                marginBottom: 12,
              }}
            >
              Para gastos operativos legítimos sin DTE (café con cliente, taxi, propinas, etc.). Se registra en{" "}
              <code>movimientos_caja</code> con <code>categoria_pago=&quot;gasto_sin_factura&quot;</code>; sin IVA
              recuperable.
            </div>

            <ProveedorSelector
              value={sfProveedorId}
              onChange={(p) => {
                setSfProveedorId(p.id);
                setSfProveedorNombre(p.nombre);
                if (p.cuenta_er_default && !sfCuentaER) setSfCuentaER(p.cuenta_er_default);
              }}
              filter={(p) => p.tipo === "comercial" || p.tipo === "interno"}
              proveedores={proveedores}
            />

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Concepto</div>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Ej: Café con cliente DIDI / Taxi a reunión Latam"
                value={sfConcepto}
                onChange={(e) => setSfConcepto(e.target.value)}
                style={{ fontSize: "0.82rem" }}
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Cuenta Estado de Resultados</div>
              <select
                className="form-select form-select-sm"
                value={sfCuentaER}
                onChange={(e) => setSfCuentaER(e.target.value)}
                style={{ fontSize: "0.82rem" }}
              >
                <option value="">Seleccionar cuenta…</option>
                {["Costos Fijos", "Costos Variables"].map((grupo) => (
                  <optgroup key={grupo} label={grupo}>
                    {CUENTAS_ER.filter((c) => c.grupo === grupo).map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Comercio (opcional)</div>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Ej: Starbucks Magdalena, Uber, Taxi Cabify…"
                value={sfComercio}
                onChange={(e) => setSfComercio(e.target.value)}
                style={{ fontSize: "0.82rem" }}
              />
              <div style={{ fontSize: "0.7rem", color: "#888", marginTop: 3 }}>
                Si lo dejás vacío, se usa el concepto como nombre de entidad.
              </div>
            </div>
          </>
        )}

        {esPagoACuenta && (
          <>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#666",
                background: "#f8f9fa",
                borderRadius: 4,
                padding: "8px 10px",
                marginBottom: 12,
              }}
            >
              Movimiento a cuenta corriente del proveedor (anticipo, retiro, devolución). No es un gasto operativo —
              afecta solo el saldo del proveedor, no el Estado de Resultados.
            </div>

            <ProveedorSelector
              value={acProveedorId}
              onChange={(p) => {
                setAcProveedorId(p.id);
                setAcProveedorNombre(p.nombre);
              }}
              filter={(p) => p.tipo === "comercial" || p.tipo === "interno"}
              proveedores={proveedores}
            />

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Nota (opcional)</div>
              <textarea
                className="form-control form-control-sm"
                placeholder="Ej: Anticipo honorarios marzo, retiro contra cuenta personal…"
                value={acNota}
                onChange={(e) => setAcNota(e.target.value)}
                style={{ fontSize: "0.82rem", minHeight: 60 }}
              />
            </div>
          </>
        )}

        {esPagoInstitucional &&
          (() => {
            const inst = INSTITUCIONES.find((i) => i.value === piInstitucion);
            const requierePeriodo = !!inst?.requierePeriodo;
            return (
              <>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#666",
                    background: "#f8f9fa",
                    borderRadius: 4,
                    padding: "8px 10px",
                    marginBottom: 12,
                  }}
                >
                  Para pagos a entidades sin DTE: PREVIRED, {enteFiscal}, Tesorería, comisiones bancarias, etc. Se
                  registra en <code>movimientos_caja</code> sin factura asociada.
                </div>

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Institución</div>
                  <select
                    className="form-select form-select-sm"
                    value={piInstitucion}
                    onChange={(e) => setPiInstitucion(e.target.value)}
                    style={{ fontSize: "0.82rem" }}
                  >
                    <option value="">Seleccionar institución…</option>
                    {INSTITUCIONES.map((i) => (
                      <option key={i.value} value={i.value}>
                        {i.label.replace(/\bSII\b/g, enteFiscal)}
                      </option>
                    ))}
                  </select>
                </div>

                {requierePeriodo && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Período que cubre</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <select
                        className="form-select form-select-sm"
                        value={piPeriodoMes || ""}
                        onChange={(e) => setPiPeriodoMes(e.target.value ? Number(e.target.value) : null)}
                        style={{ flex: 2, fontSize: "0.82rem" }}
                      >
                        <option value="">Mes…</option>
                        {MESES.map((m, i) => (
                          <option key={i} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                      <select
                        className="form-select form-select-sm"
                        value={piPeriodoAño || ""}
                        onChange={(e) => setPiPeriodoAño(e.target.value ? Number(e.target.value) : null)}
                        style={{ flex: 1, fontSize: "0.82rem" }}
                      >
                        {añosPI.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Notas (opcional)</div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Ej: Folio 12345, ajuste retroactivo, etc."
                    value={piNotas}
                    onChange={(e) => setPiNotas(e.target.value)}
                    style={{ fontSize: "0.82rem" }}
                  />
                </div>
              </>
            );
          })()}

        {esEmpleado && (
          <>
            <div style={{ fontSize: "0.82rem", fontWeight: 600, marginBottom: 6 }}>Empleado</div>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Buscar empleado..."
              value={empleadoBusqueda}
              onChange={(e) => setEmpleadoBusqueda(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            <div
              style={{
                maxHeight: 180,
                overflowY: "auto",
                border: "1px solid #dee2e6",
                borderRadius: 4,
                marginBottom: 16,
              }}
            >
              {loading && empleados.length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Cargando...</div>
              ) : empleadosFiltrados.length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin empleados</div>
              ) : (
                empleadosFiltrados.map((e) => {
                  const seleccionado = empleadoId === e.id;
                  return (
                    <div
                      key={e.id}
                      onClick={() => setEmpleadoId(e.id)}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontSize: "0.83rem",
                        borderBottom: "1px solid #f0f0f0",
                        background: seleccionado ? "#e8f4fd" : "transparent",
                        fontWeight: seleccionado ? 600 : 400,
                      }}
                    >
                      {e.nombre} {e.apellido}
                    </div>
                  );
                })
              )}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <div style={{ fontSize: "0.82rem", fontWeight: 600 }}>Desglose del pago</div>
              <Button size="sm" variant="outline-secondary" onClick={agregarComponente}>
                + Componente
              </Button>
            </div>

            {componentes.map((c, idx) => {
              const requierePeriodo = TIPOS_REQUIEREN_PERIODO.includes(c.tipo);
              return (
                <div
                  key={idx}
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    padding: 10,
                    marginBottom: 8,
                    background: "#fafbfc",
                  }}
                >
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                    <select
                      className="form-select form-select-sm"
                      value={c.tipo}
                      onChange={(ev) => actualizarComponente(idx, "tipo", ev.target.value)}
                      style={{ flex: "0 0 130px", fontSize: "0.82rem" }}
                    >
                      {TIPOS_PAGO_EMPLEADO.map((t) => (
                        <option key={t} value={t}>
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </option>
                      ))}
                    </select>
                    {requierePeriodo ? (
                      <>
                        <select
                          className="form-select form-select-sm"
                          value={c.periodo_mes || ""}
                          onChange={(ev) =>
                            actualizarComponente(idx, "periodo_mes", ev.target.value ? Number(ev.target.value) : null)
                          }
                          style={{ flex: "0 0 120px", fontSize: "0.82rem" }}
                        >
                          <option value="">Mes…</option>
                          {MESES.map((m, i) => (
                            <option key={i} value={i + 1}>
                              {m}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="Año"
                          value={c.periodo_año || ""}
                          onChange={(ev) =>
                            actualizarComponente(idx, "periodo_año", ev.target.value ? Number(ev.target.value) : null)
                          }
                          style={{ flex: "0 0 80px", fontSize: "0.82rem" }}
                        />
                      </>
                    ) : (
                      <span style={{ flex: 1, fontSize: "0.78rem", color: "#888" }}>(sin período)</span>
                    )}
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      placeholder="Monto"
                      value={c.monto}
                      onChange={(ev) => actualizarComponente(idx, "monto", ev.target.value)}
                      style={{ flex: 1, fontSize: "0.82rem", textAlign: "right" }}
                    />
                    {componentes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => eliminarComponente(idx)}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "#c0392b",
                          cursor: "pointer",
                          fontSize: "1rem",
                          padding: "0 4px",
                        }}
                        title="Eliminar componente"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Notas (opcional)"
                    value={c.notas}
                    onChange={(ev) => actualizarComponente(idx, "notas", ev.target.value)}
                    style={{ fontSize: "0.78rem" }}
                  />
                </div>
              );
            })}

            <div
              style={{
                marginTop: 10,
                padding: "8px 12px",
                borderRadius: 4,
                background: cuadra ? "#e8f5e9" : "#fdecea",
                fontSize: "0.82rem",
                display: "flex",
                justifyContent: "space-between",
                fontWeight: 600,
              }}
            >
              <span>Total componentes</span>
              <span>
                {formatAmount(totalComponentes, moneda)}{" "}
                {!cuadra && (
                  <span style={{ color: "#c0392b", marginLeft: 8 }}>
                    (diferencia {formatAmount(diferencia, moneda)})
                  </span>
                )}
              </span>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleConfirmar} disabled={confirmarDisabled}>
          {loading ? "Guardando..." : "Confirmar vinculación"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VincularMovimientoModal;
